import { htmlToSlate } from '@amelio/ui-library';
import { Node } from 'slate';
import { z } from 'zod';

import type { TranslationKey } from '@/i18n-key';

import { KeyResultUnit } from '@/constants/KeyResultUnit';
import { ObjectiveParticipantAttributeType } from '@/constants/ObjectiveParticipantAttributeType';
import { ObjectiveReminder } from '@/constants/ObjectiveReminder';
import { DEFAULT_CURRENCY_SYMBOL } from '@/utils/objectivesFormat';

export const TITLE_MAX_LENGTH = 150;
/** Counted on the visible text, like the editor's own counter; the server caps the stored HTML
 * at twice this, leaving room for the markup. */
export const DESCRIPTION_MAX_LENGTH = 5000;
export const CURRENCY_SYMBOL_MAX_LENGTH = 5;
/** The widest value a key result is measured at, either side of zero: `MaxTrackedValue` on the
 * server, where a wider one overflows the progress computation. */
const KEY_RESULT_MAX_VALUE = 1_000_000_000_000;

const TITLE_REQUIRED_MESSAGE: TranslationKey = 'objectives.form.titleRequired';
const KEY_RESULT_TITLE_REQUIRED_MESSAGE: TranslationKey = 'objectives.form.keyResultTitleRequired';
const VALUE_OUT_OF_RANGE_MESSAGE: TranslationKey = 'objectives.form.valueOutOfRange';
const TITLE_MAX_LENGTH_MESSAGE: TranslationKey = 'objectives.form.titleMaxLength';
const HOLDER_REQUIRED_MESSAGE: TranslationKey = 'objectives.form.holderRequired';
const DATE_REQUIRED_MESSAGE: TranslationKey = 'objectives.form.dateRequired';
const END_BEFORE_START_MESSAGE: TranslationKey = 'objectives.form.endBeforeStart';
const START_AFTER_END_MESSAGE: TranslationKey = 'objectives.form.startAfterEnd';
const KEY_RESULT_REQUIRED_MESSAGE: TranslationKey = 'objectives.form.keyResultRequired';
const TARGET_EQUALS_START_MESSAGE: TranslationKey = 'objectives.form.targetDifferentFromStart';
const PILLAR_CHOICE_REQUIRED_MESSAGE: TranslationKey = 'objectives.form.pillarChoiceRequired';
const PILLAR_REQUIRED_MESSAGE: TranslationKey = 'objectives.form.pillarRequired';

/** The length of a rich-text description as its editor counts it: text only, markup excluded. */
const richTextLength = (html: string): number =>
    htmlToSlate(html).reduce((length, node) => length + Node.string(node).length, 0);

/** Same four values as `KeyResultUnit` (`@/constants/KeyResultUnit`), spelled out for `z.enum`. */
const keyResultUnitValues = [
    KeyResultUnit.NUMBER,
    KeyResultUnit.PERCENTAGE,
    KeyResultUnit.MONETARY,
    KeyResultUnit.YES_NO,
] as const;

const keyResultValue = z
    .number()
    .min(-KEY_RESULT_MAX_VALUE, { message: VALUE_OUT_OF_RANGE_MESSAGE })
    .max(KEY_RESULT_MAX_VALUE, { message: VALUE_OUT_OF_RANGE_MESSAGE });

/** A key result as a draft may hold it: only what the server could not store is refused. */
const draftKeyResultFormSchema = z.object({
    /** Present only when editing an existing key result. */
    id: z.string().optional(),
    title: z.string().max(TITLE_MAX_LENGTH, { message: TITLE_MAX_LENGTH_MESSAGE }),
    unit: z.enum(keyResultUnitValues),
    currencySymbol: z.string().max(CURRENCY_SYMBOL_MAX_LENGTH).optional(),
    startValue: keyResultValue,
    targetValue: keyResultValue,
    currentValue: keyResultValue,
    responsibleEmployeeId: z.string().optional(),
});

export const keyResultFormSchema = draftKeyResultFormSchema
    .extend({
        title: z
            .string()
            .refine((title) => title.trim().length > 0, {
                message: KEY_RESULT_TITLE_REQUIRED_MESSAGE,
            })
            .max(TITLE_MAX_LENGTH, { message: TITLE_MAX_LENGTH_MESSAGE }),
    })
    .superRefine((keyResult, ctx) => {
        // A target below the start is a measure to reduce: only an empty range, where no progress
        // can be measured, is refused.
        if (
            keyResult.unit !== KeyResultUnit.YES_NO &&
            keyResult.targetValue === keyResult.startValue
        ) {
            // Reported on BOTH ends of the range: react-hook-form only surfaces the error of the
            // field that just changed, so an issue on `targetValue` alone stays invisible while
            // the user is editing `startValue` (the very field that broke the rule).
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: TARGET_EQUALS_START_MESSAGE,
                path: ['targetValue'],
            });
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: TARGET_EQUALS_START_MESSAGE,
                path: ['startValue'],
            });
        }
    });

const draftObjectiveFields = z.object({
    title: z
        .string()
        // A title made of spaces is no title.
        .refine((title) => title.trim().length > 0, { message: TITLE_REQUIRED_MESSAGE })
        .max(TITLE_MAX_LENGTH, { message: TITLE_MAX_LENGTH_MESSAGE }),
    description: z
        .string()
        .refine((html) => richTextLength(html) <= DESCRIPTION_MAX_LENGTH)
        .optional(),
    /** `null` until a choice is made; empty once "No pillar" is chosen. */
    pillarIds: z.array(z.string()).nullable(),
    employeeIds: z.array(z.string()),
    participantAttributes: z.array(
        z.object({ type: z.enum(ObjectiveParticipantAttributeType), valueId: z.string() }),
    ),
    competencyIds: z.array(z.string()),
    startDate: z.string(),
    endDate: z.string(),
    keyResults: z.array(draftKeyResultFormSchema),
    reminders: z.array(z.enum(ObjectiveReminder)),
});

const refuseEndBeforeStart = (
    objective: Pick<z.infer<typeof draftObjectiveFields>, 'startDate' | 'endDate'>,
    ctx: z.RefinementCtx,
) => {
    if (objective.startDate && objective.endDate && objective.endDate < objective.startDate) {
        // Reported on both dates, each in its own words: react-hook-form only surfaces the error of
        // the field that just changed, so an issue on the end date alone stays invisible while the
        // start date is the one being moved past it.
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: START_AFTER_END_MESSAGE,
            path: ['startDate'],
        });
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: END_BEFORE_START_MESSAGE,
            path: ['endDate'],
        });
    }
};

/** What saving a draft requires: a title, and nothing the server would refuse to store. */
export const draftObjectiveFormSchema = draftObjectiveFields.superRefine(refuseEndBeforeStart);

/**
 * What publishing checks across more than one field. An objective must be held by somebody, and
 * naming an employee is only one way: an objective aimed at a whole group carries its holders
 * through its attributes and names nobody, so neither field is required on its own. The issue lands
 * on the employee field, where the picker that fills both of them lives. Mirrors `HaveAHolder` in
 * the server's `ObjectiveWriteRequestValidator`.
 */
const refinePublishedObjective = (
    objective: Pick<
        z.infer<typeof draftObjectiveFields>,
        'employeeIds' | 'participantAttributes' | 'startDate' | 'endDate'
    >,
    ctx: z.RefinementCtx,
) => {
    if (objective.employeeIds.length === 0 && objective.participantAttributes.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: HOLDER_REQUIRED_MESSAGE,
            path: ['employeeIds'],
        });
    }

    refuseEndBeforeStart(objective, ctx);
};

const objectiveFields = draftObjectiveFields.extend({
    startDate: z.string().min(1, { message: DATE_REQUIRED_MESSAGE }),
    endDate: z.string().min(1, { message: DATE_REQUIRED_MESSAGE }),
    keyResults: z.array(keyResultFormSchema).min(1, { message: KEY_RESULT_REQUIRED_MESSAGE }),
});

/**
 * An unmade pillar choice is refused as an unanswered question rather than as a wrong type, in both
 * schemas below: a type error is fatal, and zod would stop there without ever running the
 * cross-field checks, leaving the form to report the missing pillar and stay silent about the
 * missing holder.
 */
const answeredPillarChoice = z
    .array(z.string())
    .nullable()
    .refine((pillarIds) => pillarIds !== null, { message: PILLAR_CHOICE_REQUIRED_MESSAGE });

const chosenPillar = z
    .array(z.string())
    .nullable()
    .refine((pillarIds) => !!pillarIds?.length, { message: PILLAR_REQUIRED_MESSAGE });

/** What publishing requires: the whole objective. */
export const objectiveFormSchema = objectiveFields.superRefine(refinePublishedObjective);

/** Publishing where the form offers "No pillar": nothing is assumed, a pillar or "No pillar" is picked. */
export const objectiveWithPillarChoiceFormSchema = objectiveFields
    .extend({ pillarIds: answeredPillarChoice })
    .superRefine(refinePublishedObjective);

/** Publishing where the company requires a pillar: "No pillar" is not an answer. */
export const objectiveWithRequiredPillarFormSchema = objectiveFields
    .extend({ pillarIds: chosenPillar })
    .superRefine(refinePublishedObjective);

export type KeyResultFormData = z.infer<typeof keyResultFormSchema>;
export type ObjectiveFormData = z.infer<typeof objectiveFormSchema>;

const NUMERIC_DEFAULTS = { startValue: 0, targetValue: 100, currentValue: 0 } as const;

export const DEFAULT_KEY_RESULT: KeyResultFormData = {
    title: '',
    unit: KeyResultUnit.NUMBER,
    currencySymbol: DEFAULT_CURRENCY_SYMBOL,
    ...NUMERIC_DEFAULTS,
};

/** Whether a key result row is still exactly as "Add a key result" created it: nothing to keep. */
export const isUntouchedKeyResult = (keyResult: KeyResultFormData): boolean =>
    !keyResult.id &&
    keyResult.title.trim() === '' &&
    keyResult.unit === DEFAULT_KEY_RESULT.unit &&
    keyResult.startValue === DEFAULT_KEY_RESULT.startValue &&
    keyResult.targetValue === DEFAULT_KEY_RESULT.targetValue &&
    keyResult.currentValue === DEFAULT_KEY_RESULT.currentValue &&
    !keyResult.responsibleEmployeeId;

/** Start/target/current values reset whenever the key result's unit changes. */
export const defaultsForUnit = (
    unit: KeyResultFormData['unit'],
): Pick<KeyResultFormData, 'startValue' | 'targetValue' | 'currentValue'> =>
    unit === KeyResultUnit.YES_NO
        ? { startValue: 0, targetValue: 1, currentValue: 0 }
        : { ...NUMERIC_DEFAULTS };
